import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const faqData = [
  { question: 'How is Forsyt different from Scratchpad?', answer: 'Scratchpad simplifies CRM hygiene with spreadsheet-like views of Salesforce. Forsyt is a Revenue OS that goes beyond CRM management to prescribe daily actions, identify deal blockers, and drive rep follow-through.' },
  { question: 'Can Forsyt replace Scratchpad?', answer: 'They solve different problems. Scratchpad makes CRM updates easier; Forsyt tells reps what to do next. Many teams may use both, or find Forsyt covers the action gap Scratchpad leaves open.' },
  { question: 'What does a Revenue OS do that Scratchpad doesn\'t?', answer: 'A Revenue OS like Forsyt ingests signals from CRM, calls, email, and Slack to surface stalled deals and prescribe actions. Scratchpad focuses on making Salesforce easier to use.' },
];

const ForsytVsScratchpad = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Forsyt vs Scratchpad - Revenue OS vs CRM Hygiene"
        description="Scratchpad makes Salesforce easier. Forsyt makes your reps more effective. Compare Revenue OS vs CRM tools to see which drives real pipeline outcomes."
        path="/vs-scratchpad"
        faq={faqData}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/gt687tca.png"
                alt="Forsyt"
                className="h-8 w-auto"
              />
            </div>
            <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-gray-900 mb-8">Forsyt vs Scratchpad: A Better Salesforce UI, Or a Revenue OS For Your Whole GTM Stack?</h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Scratchpad is beloved by reps for giving Salesforce a fast, flexible workspace. Forsyt goes a level up: from making CRM updates easier to making the entire revenue motion more intelligent and prescriptive.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What Scratchpad Is Optimized For</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Scratchpad markets itself as an AI workspace and modern interface that sits between sellers and Salesforce.
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Scratchpad's Strengths</h3>
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li>Provides sheets, notes, and task views that sync in real time with Salesforce.</li>
                <li>Minimizes tab‑switching and makes updates feel like using a familiar spreadsheet or notes app.</li>
                <li>Offers AI‑assisted call notes, summaries, and templates to reduce admin work.</li>
                <li>Helps individual reps and small teams stay organized and keep CRM fields updated.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Scratchpad answers:</strong> "How can I make Salesforce feel less painful for my reps?"
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Where Scratchpad Stops, It's Still A Workspace</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Even with Scratchpad:</p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li>Leadership still has to decide which deals reps should focus on, based on their own analysis.</li>
                <li>There's no orchestration across tools beyond Salesforce, email, call intelligence, Slack, and CS platforms remain disconnected.</li>
                <li>"What to do next" is still a human judgment call; the tool mainly accelerates data capture and navigation.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
                Scratchpad is a better front‑end, not a new operating model.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Forsyt, From Better UI To Better Decisions</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Forsyt's thesis is that reps and managers don't just need a nicer UI, they need fewer, clearer decisions, powered by full‑stack context.
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">How Forsyt Reframes The Problem</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Not "Where do I update?", but "Which deal should I touch now?"</h4>
                  <p className="text-gray-700">Forsyt scans CRM, conversations, and activity across tools to surface the single highest‑impact next action for each rep.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Not "How do we make reps log data?", but "How do we make data useful enough that reps want to act?"</h4>
                  <p className="text-gray-700">Forsyt turns logs into directives, tying each suggestion to a concrete revenue outcome.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Not "Is Salesforce updated?", but "Is the revenue playbook being executed?"</h4>
                  <p className="text-gray-700">Managers configure exit criteria, qualification frameworks, and CS handoff rules; Forsyt nudges and enforces them.</p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Forsyt vs Scratchpad, Side‑By‑Side</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Dimension</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Scratchpad (Workspace)</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Forsyt (Revenue OS)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Primary value</td>
                      <td className="border border-gray-300 px-4 py-3">Make Salesforce easier and faster for reps.</td>
                      <td className="border border-gray-300 px-4 py-3">Coordinate actions across CRM, email, calls, and Slack to move revenue.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Data source focus</td>
                      <td className="border border-gray-300 px-4 py-3">Primarily Salesforce; optional call/AI features.</td>
                      <td className="border border-gray-300 px-4 py-3">Multi‑tool: CRM, call intelligence, email, calendars, collaboration.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Output to reps</td>
                      <td className="border border-gray-300 px-4 py-3">Notes, tasks, lists, and Salesforce updates.</td>
                      <td className="border border-gray-300 px-4 py-3">Ranked action cards with recommended plays and ownership.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Leadership lens</td>
                      <td className="border border-gray-300 px-4 py-3">Indirect: better data hygiene, some pipeline views.</td>
                      <td className="border border-gray-300 px-4 py-3">Direct: dashboards of actions done/not done, playbook adherence, and revenue impact.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Role in stack</td>
                      <td className="border border-gray-300 px-4 py-3">UI layer in front of CRM.</td>
                      <td className="border border-gray-300 px-4 py-3">Orchestration layer over CRM and other tools.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How To Talk To Reps And Managers About The Difference</h2>
              <ol className="list-decimal pl-6 space-y-4 text-gray-700">
                <li>
                  <strong>Start with empathy:</strong><br />
                  "Scratchpad made Salesforce tolerable. Forsyt makes your pipeline intelligent, so the system does more thinking for you."
                </li>
                <li>
                  <strong>Clarify the scope:</strong><br />
                  "Scratchpad is a workspace for you; Forsyt is an operating system for the whole GTM team."
                </li>
                <li>
                  <strong>Anchor on outcomes, not features:</strong><br />
                  "If the goal is faster updates, Scratchpad is great. If the goal is more revenue from the same headcount, we need a layer that tells every rep what to do next, not just where to type."
                </li>
              </ol>
            </section>

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Try This</h3>
              <p className="mb-4">
                Run Forsyt on top of your current Salesforce workspace for one team.<br />
                Ask two questions after 30 days: "Are people updating faster?" vs "Are we closing faster?"
              </p>
              <p className="mb-6 font-semibold">
                If the second answer changes, you've found your Revenue OS.
              </p>
              <a 
                href="https://calendar.app.google/nswB9pt6ALLZ8xoy7" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300"
              >
                Book Your Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ForsytVsScratchpad;
